import type { AgentPayoutStatus } from '@evi/shared';

/**
 * What a Sales Partner reads for each commission row's status, on Dashboard and Commissions. Every status the model
 * allows has words here, so a partner never sees a raw key such as skipped_pre_qualification (Warren, 28 October 2026:
 * "Remove any developer copy a partner can see on Dashboard, My chargers and Commissions"). A status added later
 * reads "Processing" until it gets its own words.
 */
export type PayoutStatusTone = 'ok' | 'info' | 'warn' | 'muted';

export const AGENT_PAYOUT_STATUS_LABELS: Readonly<Record<AgentPayoutStatus, { label: string; tone: PayoutStatusTone }>> = {
  pending: { label: 'Pending', tone: 'info' },
  transferred: { label: 'Paid', tone: 'ok' },
  failed: { label: 'Retrying', tone: 'warn' },
  reversed: { label: 'Reversed', tone: 'muted' },
  absorbed_clawback: { label: 'Netted', tone: 'muted' },
  pending_stripe_setup: { label: 'Setup needed', tone: 'warn' },
  permanently_failed: { label: 'Failed', tone: 'muted' },
  clawback_pending: { label: 'Clawback', tone: 'muted' },
  // Before the partner qualifies (four paid launches), a session earns no commission.
  skipped_pre_qualification: { label: 'Not earning yet', tone: 'muted' },
  // A residential or EVI-sourced site, or one with no recorded origination, pays no commission.
  skipped_ineligible_site: { label: 'No commission', tone: 'muted' },
};

export function agentPayoutStatusLabel(status: string): { label: string; tone: PayoutStatusTone } {
  return Object.hasOwn(AGENT_PAYOUT_STATUS_LABELS, status)
    ? AGENT_PAYOUT_STATUS_LABELS[status as AgentPayoutStatus]
    : { label: 'Processing', tone: 'muted' };
}
